import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useMutation, useQueryClient } from 'react-query';
import axios from './axios';
import constants from './constants';
import useBroadcastChannel from './hooks/useBroadCastChannel';
import useInitializer from './hooks/useInitializer';
import { safeTrackMixpanel } from './mixpanelLogger';
import { clearCookies, getCookie } from './utilities';

enum AuthActionType {
  LOGIN = 'login',
  MANUAL_LOGOUT = 'manual-logout',
  AUTO_LOGOUT = 'auto-logout',
  CLOSE_DIALOG = 'close-dialog',
}
interface AuthAction {
  type: AuthActionType;
  payload?: any;
  senderId?: string;
}

interface AuthContextValue {
  isLoggedIn: boolean;
  login: (success: boolean) => void;
  logout: () => void;
  checkAuth: () => Promise<void>;
  initializer?: ReturnType<typeof useInitializer>;
  shouldRender: boolean;
  showSignedOutDialog: boolean;
  closeSignedOutDialog: () => void;
  isManualLogout: boolean;
  hasVersionUpdate: boolean;
  currentVersion: string | null;
  newVersion: string | null;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
  portal: string;
  enableRendering?: boolean;
  useStripe?: boolean;
}

export function AuthProvider({ children, portal, enableRendering, useStripe }: AuthProviderProps) {
  // map each portal to its corresponding refresh time environment variable name
  const portalEnvMap: Record<string, string> = {
    [constants.PORTALS.ADMIN]: 'VITE_ADMIN_PORTAL_JWT_ACCESS_TOKEN_REFRESH_SECONDS',
    [constants.PORTALS.DENTIST]: 'VITE_DENTIST_PORTAL_JWT_ACCESS_TOKEN_REFRESH_SECONDS',
    [constants.PORTALS.EMPLOYER]: 'VITE_EMPLOYER_PORTAL_JWT_ACCESS_TOKEN_REFRESH_SECONDS',
    [constants.PORTALS.MEMBER]: 'VITE_MEMBER_PORTAL_JWT_ACCESS_TOKEN_REFRESH_SECONDS',
  };

  // version-check throttling
  const HELLO_COOLDOWN_MS = 5 * 60 * 1000; // max 1 call / 5 min
  const IDLE_THRESHOLD_MS = 5 * 60 * 1000; // 5 min idle time

  // get the env variable name for the current portal
  const refreshTimeEnvVarName = portalEnvMap[portal];

  // get the actual env variable value
  const refreshTimeEnvVariable = refreshTimeEnvVarName
    ? import.meta.env[refreshTimeEnvVarName]
    : undefined;

  // set refresh time in ms, fallback to 4 minutes if not set
  const refreshTime = refreshTimeEnvVariable ? +refreshTimeEnvVariable * 1000 : 4 * 60 * 1000;
  const queryClient = useQueryClient();
  const uniqueId = useRef(Math.random().toString(36).substring(2, 15));
  const lastProcessedMessage = useRef<AuthAction | null>(null);
  const isInitialMount = useRef(true);

  // state
  const [shouldRender, setShouldRender] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isInitialLoggedIn, setIsInitialLoggedIn] = useState(false);
  const [showSignedOutDialog, setShowSignedOutDialog] = useState(false);
  const [isManualLogout, setIsManualLogout] = useState(false);
  const [hasVersionUpdate, setHasVersionUpdate] = useState(false);
  const [currentVersion, setCurrentVersion] = useState<string | null>(null);
  const [newVersion, setNewVersion] = useState<string | null>(null);
  // version-check throttling

  const lastHelloCheckAt = useRef<number>(0);
  const idleTimeoutId = useRef<number | null>(null);
  const versionCheckInProgress = useRef(false);

  // hooks
  const initializer = useInitializer({
    portal,
    useStripe,
    onAfterInit: () => (enableRendering ? setShouldRender(true) : null),
  });

  // broadcast channel hook
  const { message: authBrodcastMessage, sendMessage: sendAuthAction } =
    useBroadcastChannel<AuthAction>('auth-actions');

  // mutations
  const logoutMutation = useMutation(async () => {
    return axios.post('logout/');
  });

  const refreshMutation = useMutation(
    async () => {
      return axios.post('token/refresh/');
    },
    {
      onError: (error) => {
        throw error;
      },
    }
  );

  const silentRefresh = async () => {
    try {
      await axios.post('token/refresh/');
    } catch (err) {
      handleAutoLogout();
    }
  };

  // effect hooks
  useEffect(() => {
    checkAuth();
    isInitialMount.current = false;
  }, []);

  // set current version after initializer (/hello) fetches full response
  useEffect(() => {
    if (initializer?.fullResponse) {
      const serverVersion =
        initializer.fullResponse.source_commit || initializer.fullResponse.version_number;
      if (serverVersion) {
        const cachedVersion = sessionStorage.getItem('app_version');
        if (!cachedVersion) {
          // first load - store the server version
          sessionStorage.setItem('app_version', serverVersion);
          setCurrentVersion(serverVersion);
        } else if (cachedVersion === serverVersion) {
          // version matches (after user clicked refresh) - sync state
          setCurrentVersion(serverVersion);
        }
        // if cachedVersion !== serverVersion check version to properly show the banner
      }
    }
  }, [initializer?.fullResponse]);

  // track page visibility and check version on tab change (max once per 5 min)
  useEffect(() => {
    const handleVisibilityChange = () => {
      const isNowVisible = !document.hidden;

      if (isNowVisible) {
        // after returning to the tab: check, but gate limits to 1/5min
        maybeCheckVersion('tab-visible');

        // and reset the idle timer
        scheduleIdleCheck();
      } else {
        // when the tab is hidden, there's no point in keeping the idle timer
        clearIdleTimer();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [hasVersionUpdate]);

  // idle detection: restart timer on activity (active tab only)
  useEffect(() => {
    const onActivity = () => {
      scheduleIdleCheck();
    };

    window.addEventListener('mousemove', onActivity);
    window.addEventListener('mousedown', onActivity);
    window.addEventListener('keydown', onActivity);
    window.addEventListener('scroll', onActivity, { passive: true });
    window.addEventListener('touchstart', onActivity, { passive: true });

    // start timer on mount if tab is visible
    scheduleIdleCheck();

    return () => {
      window.removeEventListener('mousemove', onActivity);
      window.removeEventListener('mousedown', onActivity);
      window.removeEventListener('keydown', onActivity);
      window.removeEventListener('scroll', onActivity as any);
      window.removeEventListener('touchstart', onActivity as any);
      clearIdleTimer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // handle auth messages
  useEffect(() => {
    if (!authBrodcastMessage) return;

    // if the message is the same as the last processed message, skip processing
    if (
      lastProcessedMessage.current &&
      lastProcessedMessage.current.type === authBrodcastMessage.type &&
      lastProcessedMessage.current.senderId === authBrodcastMessage.senderId &&
      JSON.stringify(lastProcessedMessage.current.payload) ===
        JSON.stringify(authBrodcastMessage.payload)
    ) {
      return;
    }

    // mark this message as processed
    lastProcessedMessage.current = authBrodcastMessage;

    if (authBrodcastMessage.senderId === uniqueId.current) return;

    switch (authBrodcastMessage.type) {
      case AuthActionType.LOGIN:
        checkAuth();
        break;
      case AuthActionType.MANUAL_LOGOUT:
        setIsLoggedIn(false);
        setIsInitialLoggedIn(false);
        if (window.location.hash !== '#/sign-in') {
          window.location.hash = '/sign-in';
        }
        break;
      case AuthActionType.AUTO_LOGOUT:
        setShowSignedOutDialog(true);
        setIsLoggedIn(false);
        setIsInitialLoggedIn(false);
        break;
      case AuthActionType.CLOSE_DIALOG:
        setShowSignedOutDialog(false);
        break;
    }
  }, [authBrodcastMessage]);

  useEffect(() => {
    let intervalId: number | null = null;

    if (isInitialLoggedIn) {
      intervalId = window.setInterval(() => {
        const hasCurrentRefresh = Boolean(getCookie(constants.COOKIES.REFRESH));
        if (!hasCurrentRefresh) {
          clearInterval(intervalId!);
          handleAutoLogout();
          return;
        }
        if (navigator.onLine) silentRefresh();
      }, refreshTime);
    }

    if (!isInitialLoggedIn && intervalId) {
      clearInterval(intervalId);
      intervalId = null;
    }

    if (authBrodcastMessage?.type === AuthActionType.MANUAL_LOGOUT && intervalId) {
      clearInterval(intervalId);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [isInitialLoggedIn, authBrodcastMessage]);

  // pass autologout handler to axios
  useEffect(() => {
    axios.setAutoLogoutHandler(handleAutoLogout);
    return () => {
      axios.setAutoLogoutHandler(() => {});
    };
  }, []);

  const checkAuth = async () => {
    try {
      const currentHasRefresh = Boolean(getCookie(constants.COOKIES.REFRESH));
      const currentHasAccess = Boolean(getCookie(constants.COOKIES.ACCESS));

      if (currentHasAccess && currentHasRefresh) {
        setIsLoggedIn(true);
        setIsInitialLoggedIn(true);
      }

      if (!currentHasAccess && currentHasRefresh) {
        setIsLoggedIn(true);
        setIsInitialLoggedIn(true);
        await refreshMutation.mutateAsync();
      }
    } catch (error) {
      await logoutMutation.mutateAsync();
      setIsLoggedIn(false);
      setIsInitialLoggedIn(false);
    }
  };

  const checkVersion = async () => {
    if (versionCheckInProgress.current) return;

    versionCheckInProgress.current = true;

    try {
      const response = await axios.get('/hello/');
      const serverVersion = response.data.source_commit || response.data.version_number;

      if (!serverVersion) {
        versionCheckInProgress.current = false;
        return;
      }

      // Get cached version from sessionStorage
      const cachedVersion = sessionStorage.getItem('app_version');

      if (!cachedVersion) {
        // First time - store the version
        sessionStorage.setItem('app_version', serverVersion);
        setCurrentVersion(serverVersion);
      } else if (cachedVersion !== serverVersion) {
        setCurrentVersion(cachedVersion);
        setNewVersion(serverVersion);
        setHasVersionUpdate(true);
        sessionStorage.setItem('app_version', serverVersion);
      }
    } catch (error) {
      // Silently fail - don't disrupt the user experience
      console.warn('Version check failed:', error);
    } finally {
      versionCheckInProgress.current = false;
    }
  };

  const clearIdleTimer = () => {
    if (idleTimeoutId.current) {
      window.clearTimeout(idleTimeoutId.current);
      idleTimeoutId.current = null;
    }
  };

  const scheduleIdleCheck = () => {
    // only active tab
    if (document.hidden) return;

    clearIdleTimer();
    idleTimeoutId.current = window.setTimeout(() => {
      // after 5 min of inactivity, try to do a check (through gate)
      maybeCheckVersion('idle');
    }, IDLE_THRESHOLD_MS);
  };

  const maybeCheckVersion = (reason: 'idle' | 'tab-visible') => {
    // if new version update, usually no point in further loading /hello
    if (hasVersionUpdate) return;

    // only when tab is visible
    if (document.hidden) return;

    // not in parallel
    if (versionCheckInProgress.current) return;

    const now = Date.now();

    // cooldown: blocks all triggers in the window for 5 min
    if (now - lastHelloCheckAt.current < HELLO_COOLDOWN_MS) return;

    // set timestamp before the request to avoid race conditions on events
    lastHelloCheckAt.current = now;

    // actual call
    checkVersion();
  };

  const login = useCallback(
    (success: boolean) => {
      if (success) {
        setIsLoggedIn(true);
        setIsInitialLoggedIn(true);
        sendAuthAction({ type: AuthActionType.LOGIN, senderId: uniqueId.current });

        // reload only when a new version was detected while the tab was open
        if (hasVersionUpdate) {
          sessionStorage.removeItem('app_version');
          window.location.reload();
          return;
        }
      }
      sendAuthAction({ type: AuthActionType.LOGIN, senderId: uniqueId.current });
    },
    [hasVersionUpdate]
  );

  const logout = useCallback(() => {
    setIsManualLogout(true);
    clearCookies([constants.COOKIES.ACCESS, constants.COOKIES.REFRESH]);
    setIsInitialLoggedIn(false);
    setIsLoggedIn(false);
    sendAuthAction({
      type: AuthActionType.MANUAL_LOGOUT,
      senderId: uniqueId.current,
    });
    queryClient.clear();
    logoutMutation.mutate();
    safeTrackMixpanel('manual-logout');
    window.location.hash = '/sign-in';
  }, []);

  const handleAutoLogout = () => {
    setIsLoggedIn(false);
    setIsInitialLoggedIn(false);
    if (!isInitialMount.current && isInitialLoggedIn) {
      setShowSignedOutDialog(true);
    }
    sendAuthAction({
      type: AuthActionType.AUTO_LOGOUT,
      senderId: uniqueId.current,
    });
    safeTrackMixpanel('auto-logout');
    queryClient.clear();
    logoutMutation.mutate();
  };

  const closeSignedOutDialog = () => {
    setShowSignedOutDialog(false);
    // redirect to sign-in page when refresh token expires during sign-up flow in dentist portal
    if (window.location.hash === '#/sign-up') {
      window.location.hash = '/sign-in';
    }
    sendAuthAction({
      type: AuthActionType.CLOSE_DIALOG,
      senderId: uniqueId.current,
    });
  };

  const value: AuthContextValue = useMemo(
    () => ({
      isLoggedIn,
      login,
      logout,
      checkAuth,
      initializer,
      shouldRender,
      showSignedOutDialog,
      closeSignedOutDialog,
      isManualLogout,
      hasVersionUpdate,
      currentVersion,
      newVersion,
    }),
    [
      isLoggedIn,
      login,
      logout,
      checkAuth,
      initializer,
      shouldRender,
      showSignedOutDialog,
      closeSignedOutDialog,
      isManualLogout,
      hasVersionUpdate,
      currentVersion,
      newVersion,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
